import { cn } from '@/lib/utils';
import { useDappBuilderStore } from '@/store/dappBuilderStore';
import type { DappComponentBlock, DappComponentType } from '@/types/dapp-builder';
import {
    ArrowLeftRight,
    BarChart3,
    Box,
    Coins,
    CreditCard,
    FileText,
    Footprints,
    GripVertical,
    Image,
    LayoutGrid,
    LineChart,
    LinkIcon,
    List,
    Navigation,
    PenTool,
    Plus,
    QrCode,
    Send,
    Trash2,
    Trophy,
    Type,
    Vote,
    Wallet,
    Waves,
} from 'lucide-react';
import { useCallback } from 'react';

/* ================================================================== */
/*  Component Palette (left sidebar)                                  */
/* ================================================================== */

interface PaletteItem {
  type: DappComponentType;
  label: string;
  icon: React.ElementType;
  category: 'web3' | 'layout' | 'content';
}

const PALETTE_ITEMS: PaletteItem[] = [
  // Web3 components
  { type: 'wallet-connect', label: 'Wallet Connect', icon: Wallet, category: 'web3' },
  { type: 'token-balance', label: 'Token Balance', icon: Coins, category: 'web3' },
  { type: 'nft-gallery', label: 'NFT Gallery', icon: Image, category: 'web3' },
  { type: 'swap-widget', label: 'Swap Widget', icon: ArrowLeftRight, category: 'web3' },
  { type: 'stake-panel', label: 'Stake Panel', icon: BarChart3, category: 'web3' },
  { type: 'mint-button', label: 'Mint Button', icon: PenTool, category: 'web3' },
  { type: 'transaction-history', label: 'Transactions', icon: FileText, category: 'web3' },
  { type: 'contract-read', label: 'Contract Read', icon: FileText, category: 'web3' },
  { type: 'contract-write', label: 'Contract Write', icon: Send, category: 'web3' },
  { type: 'chain-selector', label: 'Chain Selector', icon: LinkIcon, category: 'web3' },
  { type: 'address-display', label: 'Address Display', icon: CreditCard, category: 'web3' },
  { type: 'token-transfer', label: 'Token Transfer', icon: Send, category: 'web3' },
  { type: 'proposal-list', label: 'Proposals', icon: Vote, category: 'web3' },
  { type: 'vote-panel', label: 'Vote Panel', icon: Vote, category: 'web3' },
  { type: 'price-chart', label: 'Price Chart', icon: LineChart, category: 'web3' },
  { type: 'token-list', label: 'Token List', icon: List, category: 'web3' },
  { type: 'liquidity-pool', label: 'Liquidity Pool', icon: Waves, category: 'web3' },
  { type: 'leaderboard', label: 'Leaderboard', icon: Trophy, category: 'web3' },
  { type: 'qr-payment', label: 'QR Payment', icon: QrCode, category: 'web3' },
  // Layout
  { type: 'navbar', label: 'Navbar', icon: Navigation, category: 'layout' },
  { type: 'footer', label: 'Footer', icon: Footprints, category: 'layout' },
  { type: 'layout-grid', label: 'Grid', icon: LayoutGrid, category: 'layout' },
  { type: 'card', label: 'Card', icon: Box, category: 'layout' },
  // Content
  { type: 'heading', label: 'Heading', icon: Type, category: 'content' },
  { type: 'text', label: 'Text', icon: Type, category: 'content' },
  { type: 'image', label: 'Image', icon: Image, category: 'content' },
];

/* ================================================================== */
/*  Canvas — Drop Zone Grid                                           */
/* ================================================================== */

function BuilderCanvas() {
  const {
    pages,
    activePage,
    addComponent,
    removeComponent,
    selectedComponent,
    setSelectedComponent,
  } = useDappBuilderStore();

  const currentPage = pages.find((p) => p.id === activePage);
  if (!currentPage) return null;

  const handleDrop = useCallback(
    (e: React.DragEvent, row: number, col: number) => {
      e.preventDefault();
      const type = e.dataTransfer.getData('component-type') as DappComponentType;
      if (!type || !activePage) return;

      const paletteItem = PALETTE_ITEMS.find((p) => p.type === type);
      const block: DappComponentBlock = {
        id: '', // store will assign
        type,
        label: paletteItem?.label ?? type,
        props: {},
        slot: { row, col, rowSpan: 1, colSpan: type === 'navbar' || type === 'footer' ? 4 : 1 },
      };
      addComponent(activePage, block);
    },
    [activePage, addComponent],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Render a 6-row x 4-col grid
  const rows = 8;
  const cols = 4;

  return (
    <div className="flex-1 bg-[#1a1a1a] rounded-lg border border-[#333] p-4 overflow-auto">
      {/* Page tab */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] text-[#888]">Page:</span>
        <span className="text-xs text-white font-medium">{currentPage.name}</span>
        <span className="text-[10px] text-[#555]">({currentPage.path})</span>
      </div>

      {/* Grid */}
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 80px)`,
        }}
      >
        {/* Placed components */}
        {currentPage.components.map((comp) => (
          <div
            key={comp.id}
            onClick={() => setSelectedComponent(comp.id)}
            className={cn(
              'relative flex items-center justify-center rounded-md border-2 border-dashed cursor-pointer transition-all group',
              selectedComponent === comp.id
                ? 'border-indigo-500 bg-indigo-500/10'
                : 'border-[#444] bg-[#252526] hover:border-[#666]',
            )}
            style={{
              gridRow: `${comp.slot.row} / span ${comp.slot.rowSpan ?? 1}`,
              gridColumn: `${comp.slot.col} / span ${comp.slot.colSpan ?? 1}`,
            }}
          >
            <div className="flex flex-col items-center gap-1">
              <GripVertical className="w-3 h-3 text-[#555]" />
              <span className="text-[9px] text-[#aaa]">{comp.label}</span>
            </div>
            {selectedComponent === comp.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (activePage) removeComponent(activePage, comp.id);
                }}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-3 h-3 text-white" />
              </button>
            )}
          </div>
        ))}

        {/* Empty grid cells for drops */}
        {Array.from({ length: rows * cols }).map((_, i) => {
          const row = Math.floor(i / cols) + 1;
          const col = (i % cols) + 1;
          // Skip cells occupied by components
          const isOccupied = currentPage.components.some(
            (c) =>
              row >= c.slot.row &&
              row < c.slot.row + (c.slot.rowSpan ?? 1) &&
              col >= c.slot.col &&
              col < c.slot.col + (c.slot.colSpan ?? 1),
          );
          if (isOccupied) return null;
          return (
            <div
              key={`cell-${row}-${col}`}
              onDrop={(e) => handleDrop(e, row, col)}
              onDragOver={handleDragOver}
              className="flex items-center justify-center rounded border border-dashed border-[#2a2a2a] hover:border-[#555] hover:bg-[#252526]/50 transition-all"
              style={{ gridRow: row, gridColumn: col }}
            >
              <Plus className="w-3 h-3 text-[#333]" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Component Palette Sidebar                                         */
/* ================================================================== */

function ComponentPalette() {
  const categories: { id: string; label: string }[] = [
    { id: 'web3', label: 'Web3' },
    { id: 'layout', label: 'Layout' },
    { id: 'content', label: 'Content' },
  ];

  const handleDragStart = (e: React.DragEvent, type: DappComponentType) => {
    e.dataTransfer.setData('component-type', type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="w-52 shrink-0 bg-[#1e1e1e] border-r border-[#333] overflow-y-auto p-3">
      <p className="text-[10px] font-medium text-[#888] uppercase tracking-wider mb-3">
        Components
      </p>
      {categories.map((cat) => (
        <div key={cat.id} className="mb-4">
          <p className="text-[9px] font-medium text-[#555] uppercase mb-1.5">{cat.label}</p>
          <div className="space-y-1">
            {PALETTE_ITEMS.filter((p) => p.category === cat.id).map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.type}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.type)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded cursor-grab hover:bg-[#333] transition-colors active:cursor-grabbing"
                >
                  <Icon className="w-3.5 h-3.5 text-[#888]" />
                  <span className="text-[10px] text-[#ccc]">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  Properties Panel (right sidebar)                                  */
/* ================================================================== */

function PropertiesPanel() {
  const { pages, activePage, selectedComponent, updateComponent } = useDappBuilderStore();

  const currentPage = pages.find((p) => p.id === activePage);
  const comp = currentPage?.components.find((c) => c.id === selectedComponent);

  if (!comp) {
    return (
      <div className="w-56 shrink-0 bg-[#1e1e1e] border-l border-[#333] p-3 flex items-center justify-center">
        <p className="text-[10px] text-[#555]">Select a component to edit</p>
      </div>
    );
  }

  return (
    <div className="w-56 shrink-0 bg-[#1e1e1e] border-l border-[#333] p-3 overflow-y-auto">
      <p className="text-[10px] font-medium text-[#888] uppercase tracking-wider mb-3">
        Properties
      </p>

      <div className="space-y-3">
        {/* Type (read-only) */}
        <div>
          <label className="text-[9px] text-[#666]">Type</label>
          <p className="text-xs text-white mt-0.5">{comp.type}</p>
        </div>

        {/* Label */}
        <div>
          <label className="text-[9px] text-[#666]">Label</label>
          <input
            type="text"
            value={comp.label}
            onChange={(e) => {
              if (activePage) updateComponent(activePage, comp.id, { label: e.target.value });
            }}
            className="w-full mt-0.5 bg-[#2a2a2a] border border-[#444] rounded px-2 py-1 text-[10px] text-white outline-none focus:border-indigo-500"
          />
        </div>

        {/* Grid position */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] text-[#666]">Row</label>
            <input
              type="number"
              min={1}
              max={8}
              value={comp.slot.row}
              onChange={(e) => {
                if (activePage)
                  updateComponent(activePage, comp.id, {
                    slot: { ...comp.slot, row: Number(e.target.value) },
                  });
              }}
              className="w-full mt-0.5 bg-[#2a2a2a] border border-[#444] rounded px-2 py-1 text-[10px] text-white outline-none"
            />
          </div>
          <div>
            <label className="text-[9px] text-[#666]">Col</label>
            <input
              type="number"
              min={1}
              max={4}
              value={comp.slot.col}
              onChange={(e) => {
                if (activePage)
                  updateComponent(activePage, comp.id, {
                    slot: { ...comp.slot, col: Number(e.target.value) },
                  });
              }}
              className="w-full mt-0.5 bg-[#2a2a2a] border border-[#444] rounded px-2 py-1 text-[10px] text-white outline-none"
            />
          </div>
        </div>

        {/* Span */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] text-[#666]">Row Span</label>
            <input
              type="number"
              min={1}
              max={4}
              value={comp.slot.rowSpan ?? 1}
              onChange={(e) => {
                if (activePage)
                  updateComponent(activePage, comp.id, {
                    slot: { ...comp.slot, rowSpan: Number(e.target.value) },
                  });
              }}
              className="w-full mt-0.5 bg-[#2a2a2a] border border-[#444] rounded px-2 py-1 text-[10px] text-white outline-none"
            />
          </div>
          <div>
            <label className="text-[9px] text-[#666]">Col Span</label>
            <input
              type="number"
              min={1}
              max={4}
              value={comp.slot.colSpan ?? 1}
              onChange={(e) => {
                if (activePage)
                  updateComponent(activePage, comp.id, {
                    slot: { ...comp.slot, colSpan: Number(e.target.value) },
                  });
              }}
              className="w-full mt-0.5 bg-[#2a2a2a] border border-[#444] rounded px-2 py-1 text-[10px] text-white outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Main Visual Builder Component                                     */
/* ================================================================== */

export default function DappVisualBuilder() {
  return (
    <div className="flex h-[60vh] -m-5 mt-0">
      <ComponentPalette />
      <BuilderCanvas />
      <PropertiesPanel />
    </div>
  );
}
